import { Spin } from "antd";
import { FC, lazy, useLayoutEffect, useState } from "react";
import { createBrowserRouter, Navigate, RouteObject, RouterProvider } from "react-router";

import { useMeQuery } from "@/graphql";

import AdminLayout from "@/components/AdminLayout";

import CustomerLayout from "@/components/CustomerLayout";
import UserSettingsPageWrapper from "@/components/consumer/Profile/UserSettingsPageWrapper";
import { LoadingPage } from "@/pages/loading";

const Login = lazy<FC>(() => import("@/pages/authorisation"));
// const Dashboard = lazy<FC>(() => import("@/pages/admin/dashboard"));
const Courses = lazy<FC>(() => import("@/pages/admin/courses"));
const Programs = lazy<FC>(() => import("@/pages/admin/programs"));
const Users = lazy<FC>(() => import("@/pages/admin/users"));
const Notifications = lazy<FC>(() => import("@/pages/admin/notifications"));
const CreateUser = lazy<FC>(() => import("@/pages/admin/create-user"));
const CreateNotification = lazy<FC>(() => import("@/pages/admin/create-notification"));
const EditNotification = lazy<FC>(() => import("@/pages/admin/edit-notification"));
const EditUser = lazy<FC>(() => import("@/pages/admin/edit-user"));
const ViewUser = lazy<FC>(() => import("@/pages/admin/view-user"));
const ViewCourse = lazy<FC>(() => import("@/pages/admin/view-course"));
const ViewProgram = lazy<FC>(() => import("@/pages/admin/view-program"));
const MyDashaboard = lazy<FC>(() => import("@/pages/consumer/dashaboard"));
const MyLearning = lazy<FC>(() => import("@/pages/consumer/courses"));
const CourseChapters = lazy<FC>(() => import("@/pages/consumer/course-chapters"));
const ProgramCourses = lazy<FC>(() => import("@/pages/consumer/program-courses"));
const ProgramCourseChapters = lazy<FC>(() => import("@/pages/consumer/program-course-chapters"));
const CourseBuilder = lazy<FC>(() => import("@/pages/admin/course-builder"));
const TranslationReview = lazy<FC>(() => import("@/pages/admin/translation-review"));
const ProgramTranslationReview = lazy<FC>(() => import("@/pages/admin/program-translation-review"));
const ProgramBuilder = lazy<FC>(() => import("@/pages/admin/program-builder"));
const ChapterPage = lazy<FC>(() => import("@/pages/consumer/chapter"));
const PostFeedPage = lazy<FC>(() => import("@/pages/consumer/post-feed"));
const AccountPage = lazy<FC>(() => import("@/pages/consumer/user/account"));
const PasswordPage = lazy<FC>(() => import("@/pages/consumer/user/password"));
const LanguagePage = lazy<FC>(() => import("@/pages/consumer/user/language"));
const NotFound = lazy<FC>(() => import("@/pages/not-found"));
const LegalPage = lazy<FC>(() => import("@/pages/consumer/user/legal"));
const NotificationsPage = lazy<FC>(() => import("@/pages/consumer/user/notifications"));
const AdminPostsPage = lazy<FC>(() => import("@/pages/admin/posts"));
const CreatePost = lazy<FC>(() => import("@/pages/admin/create-post"));
const EditPost = lazy<FC>(() => import("@/pages/admin/edit-post"));
const ViewPost = lazy<FC>(() => import("@/pages/admin/view-post"));
const ActivityLog = lazy<FC>(() => import("@/pages/admin/activity-log"));
const ProtectedTerms = lazy<FC>(() => import("@/pages/admin/protected-terms"));
const ReportsCoursesPage = lazy<FC>(() => import("@/pages/admin/reports-courses"));
const ReportsUsersPage = lazy<FC>(() => import("@/pages/admin/reports-users"));
const ViewCourseReport = lazy<FC>(() => import("@/pages/admin/view-course-report"));
const ViewUserReport = lazy<FC>(() => import("@/pages/admin/view-user-report"));
const ViewUserCourseReport = lazy<FC>(() => import("@/pages/admin/view-user-course-report"));
const ReportsProgramsPage = lazy<FC>(() => import("@/pages/admin/reports-programs"));
const ViewProgramReport = lazy<FC>(() => import("@/pages/admin/view-program-report"));
const ViewProgramCourseReport = lazy<FC>(() => import("@/pages/admin/view-program-course-report"));
const ViewUserProgramReport = lazy<FC>(() => import("@/pages/admin/view-user-program-report"));
const TermsPage = lazy<FC>(() => import("@/pages/terms"));
const PrivacyPolicyPage = lazy<FC>(() => import("@/pages/privacy-policy"));

export const DEFAULT_ADMIN_ROUTE = "/admin/programs";

export const DEFAULT_CUSTOMER_ROUTE = "/explore/my-learning";
export const DEFAULT_SETTINGS_ROUTE = "/explore/user/account";

const loadingRoutes: RouteObject[] = [
  {
    path: "*",
    Component: LoadingPage,
  },
];

const legalRoutes: RouteObject[] = [
  {
    path: "/terms-and-conditions",
    Component: TermsPage,
  },
  {
    path: "/privacy-policy",
    Component: PrivacyPolicyPage,
  },
];

const publicRoutes: RouteObject[] = [
  {
    index: true,
    Component: () => <Navigate to={"/login"} />,
  },
  ...legalRoutes,
  {
    path: "/login",
    Component: Login,
  },
  {
    path: "*",
    Component: () => <Navigate to={"/login"} />,
  },
];

const customerRoutes: RouteObject[] = [
  {
    index: true,
    Component: () => <Navigate to={DEFAULT_CUSTOMER_ROUTE} />,
  },
  {
    path: "/login",
    Component: () => <Navigate to={DEFAULT_CUSTOMER_ROUTE} />,
  },
  {
    path: "/explore",
    Component: CustomerLayout,
    children: [
      {
        index: true,
        Component: () => <Navigate to={DEFAULT_CUSTOMER_ROUTE} />,
      },
      {
        path: "*",
        Component: NotFound,
      },
      {
        path: "dashboard",
        Component: MyDashaboard,
      },
      {
        path: "my-learning",
        Component: MyLearning,
      },
      {
        path: "courses/:courseId",
        Component: CourseChapters,
      },
      {
        path: "courses/:courseId/chapter/:chapterId",
        Component: ChapterPage,
      },
      {
        path: "program/:programId",
        Component: ProgramCourses,
      },
      {
        path: "program/:programId/course/:courseId",
        Component: ProgramCourseChapters,
      },
      {
        path: "program/:programId/course/:courseId/chapter/:chapterId",
        Component: ChapterPage,
      },
      {
        path: "user",
        Component: UserSettingsPageWrapper,
        children: [
          { path: "account", Component: AccountPage },
          { path: "language", Component: LanguagePage },
          { path: "password", Component: PasswordPage },
          { path: "notifications", Component: NotificationsPage },
          { path: "legal", Component: LegalPage },
        ],
      },
      { path: "post-feed", Component: PostFeedPage },
    ],
  },
  {
    path: "*",
    Component: NotFound,
  },
];

const creatorRoutes: RouteObject[] = [
  {
    index: true,
    Component: () => <Navigate to={DEFAULT_ADMIN_ROUTE} />,
  },
  {
    path: "/login",
    Component: () => <Navigate to={DEFAULT_ADMIN_ROUTE} />,
  },
  {
    path: "/admin",
    Component: AdminLayout,
    loader: () => <Spin spinning />,
    children: [
      {
        index: true,
        Component: () => <Navigate to={DEFAULT_ADMIN_ROUTE} />,
      },
      {
        path: "*",
        Component: () => <Navigate to={DEFAULT_ADMIN_ROUTE} />,
      },
      // {
      //   path: "dashboard",
      //   Component: Dashboard,
      // },
      {
        path: "courses",
        Component: Courses,
      },
      {
        path: "programs",
        Component: Programs,
      },
      {
        path: "program/:programId",
        Component: ViewProgram,
      },
      {
        path: "course/:courseId",
        Component: ViewCourse,
      },
    ],
  },
  {
    path: "/admin/course-builder/:courseId?",
    Component: CourseBuilder,
    loader: () => <Spin spinning />,
  },
  {
    path: "/admin/translation-review/course/:courseId",
    Component: TranslationReview,
    loader: () => <Spin spinning />,
  },
  {
    path: "/admin/translation-review/program/:programId",
    Component: ProgramTranslationReview,
    loader: () => <Spin spinning />,
  },
  {
    path: "/admin/program-builder/:programId?",
    Component: ProgramBuilder,
    loader: () => <Spin spinning />,
  },
];

const adminRoutes: RouteObject[] = [
  {
    index: true,
    Component: () => <Navigate to={DEFAULT_ADMIN_ROUTE} />,
  },
  {
    path: "/login",
    Component: () => <Navigate to={DEFAULT_ADMIN_ROUTE} />,
  },
  {
    path: "/admin",
    Component: AdminLayout,
    loader: () => <Spin spinning />,
    children: [
      {
        index: true,
        Component: () => <Navigate to={DEFAULT_ADMIN_ROUTE} />,
      },
      {
        path: "*",
        Component: NotFound,
      },
      // {
      //   path: "dashboard",
      //   Component: Dashboard,
      // },
      {
        path: "courses",
        Component: Courses,
      },
      {
        path: "course/:courseId",
        Component: ViewCourse,
      },
      {
        path: "programs",
        Component: Programs,
      },
      {
        path: "program/:programId",
        Component: ViewProgram,
      },
      {
        path: "posts",
        Component: AdminPostsPage,
      },
      {
        path: "posts/create",
        Component: CreatePost,
      },
      {
        path: "posts/edit/:postId",
        Component: EditPost,
      },
      {
        path: "posts/view/:postId",
        Component: ViewPost,
      },
      {
        path: "users",
        Component: Users,
      },
      {
        path: "users/create",
        Component: CreateUser,
      },
      {
        path: "users/edit/:userId",
        Component: EditUser,
      },
      {
        path: "users/view/:userId",
        Component: ViewUser,
      },
      {
        path: "reports/programs",
        Component: ReportsProgramsPage,
      },
      { path: "program-report/:programId", Component: ViewProgramReport },
      { path: "program-report/:programId/course/:courseId", Component: ViewProgramCourseReport },
      {
        path: "reports/courses",
        Component: ReportsCoursesPage,
      },
      { path: "course-report/:courseId", Component: ViewCourseReport },
      {
        path: "reports/users",
        Component: ReportsUsersPage,
      },
      { path: "user-report/:userId", Component: ViewUserReport },
      { path: "user-report/:userId/course/:courseId", Component: ViewUserCourseReport },
      { path: "user-report/:userId/program/:programId", Component: ViewUserProgramReport },
      { path: "notifications", Component: Notifications },
      { path: "protected-terms", Component: ProtectedTerms },
      {
        path: "notifications/create-notification",
        Component: CreateNotification,
      },
      {
        path: "notifications/edit-notification/:notificationId",
        Component: EditNotification,
      },
      {
        path: "activity-log",
        Component: ActivityLog,
      },
    ],
  },
  {
    path: "/admin/course-builder/:courseId?",
    Component: CourseBuilder,
    loader: () => <Spin spinning />,
  },
  {
    path: "/admin/translation-review/course/:courseId",
    Component: TranslationReview,
    loader: () => <Spin spinning />,
  },
  {
    path: "/admin/translation-review/program/:programId",
    Component: ProgramTranslationReview,
    loader: () => <Spin spinning />,
  },
  {
    path: "/admin/program-builder/:programId?",
    Component: ProgramBuilder,
    loader: () => <Spin spinning />,
  },
];

type UserRouteRoleType = "admin" | "creator" | "customer" | "public";

const baseCustomerRoutes = customerRoutes.filter((r) => !r.index && r.path !== "/login" && r.path !== "*");

export const roleRoutes: Record<UserRouteRoleType, RouteObject[]> = {
  public: publicRoutes,
  customer: [...legalRoutes, ...customerRoutes],
  creator: [...legalRoutes, ...baseCustomerRoutes, ...creatorRoutes],
  admin: [...legalRoutes, ...baseCustomerRoutes, ...adminRoutes],
};

const roleTypeMap: Record<string, UserRouteRoleType> = {
  super_admin: "admin",
  external_admin: "admin",
  internal_admin: "admin",
  external_creator: "creator",
  internal_creator: "creator",
  key_stylist: "customer",
  regular_stylist: "customer",
  consumer: "customer",
  internal_user: "customer",
};

const getRoleFromType = (type: Maybe<string>): UserRouteRoleType => roleTypeMap[type || ""] || "public";

export const Routes: FC = () => {
  const skipMeQuery = localStorage.getItem("accessToken") === null || localStorage.getItem("refreshToken") === null;

  const { data, loading } = useMeQuery({ skip: skipMeQuery });
  const [routes, setRoutes] = useState<RouteObject[]>(loadingRoutes);

  useLayoutEffect(() => {
    if (loading) return;

    const tokens = localStorage.getItem("accessToken") && localStorage.getItem("refreshToken");
    const user = data?.me;
    const role = getRoleFromType(user?.role?.type);

    const auth = !!tokens && !!user?.confirmed && role !== "public";

    if (!auth) {
      setRoutes(roleRoutes["public"]);
      return;
    }

    const availableRoutes = roleRoutes[role] ?? roleRoutes["customer"];

    setRoutes(availableRoutes);
  }, [data, loading]);

  return <RouterProvider router={createBrowserRouter(routes)} />;
};
